import React, { useEffect, useState, useContext, useCallback } from "react";
import { ThemeContext } from "../context/ThemeContext"; // Use ThemeContext
import { Typography, Box, Card, CardContent, Grid } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { getCarbonCredits } from "../services/api";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [credits, setCredits] = useState([]);
    const [totalEarned, setTotalEarned] = useState(0);
    const [totalAvailable, setTotalAvailable] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [chartData, setChartData] = useState(null);
    const { theme } = useContext(ThemeContext); // Get ThemeContext
    //const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

    const fetchCarbonCredits = useCallback(async () => {
        try {
            const { data } = await getCarbonCredits();
            setCredits(data);
    
            let earned = 0, available = 0, revenue = 0;
            data.forEach((credit) => {
                earned += credit.creditsEarned;
                available += credit.creditsAvailable;
                revenue += credit.revenueGenerated;
            });
    
            setTotalEarned(earned);
            setTotalAvailable(available);
            setTotalRevenue(revenue);
    
            const labels = data.map((credit, index) => `Entry ${index + 1}`);
            const earnedCredits = data.map((credit) => credit.creditsEarned);
            const availableCredits = data.map((credit) => credit.creditsAvailable);
    
            setChartData({
                labels,
                datasets: [
                    {
                        label: "Credits Earned",
                        data: earnedCredits,
                        backgroundColor: theme === "dark" ? "#AFC9A5" : "#8AA172",
                    },
                    {
                        label: "Credits Available",
                        data: availableCredits,
                        backgroundColor: theme === "dark" ? "#D2C7AE" : "#B0A084",
                    },
                ],
            });
        } catch (error) {
            console.error("Error fetching carbon credits", error);
        }
    }, [theme]); // ✅ Ensures it only updates when `theme` changes
    
    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
        fetchCarbonCredits();
    }, [fetchCarbonCredits]);
    return (
        <Box
            sx={{
                flexGrow: 1,
                padding: "20px",
                background: theme === "dark"
                    ? "linear-gradient(135deg, #6e7864, #53574f)"
                    : "linear-gradient(135deg, #d3d7cf, #e4e7e1)",
                color: theme === "dark" ? "#ffffff" : "#000000",
                minHeight: "100vh",
            }}
        >
            <Box
                sx={{
                    background: theme === "dark" ? "#4E5548" : "#F2F3EF",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: theme === "dark" ? "0px 2px 10px rgba(0, 0, 0, 0.5)" : "0px 2px 10px rgba(0, 0, 0, 0.1)",
                }}
            >

                {/* Sexy KPI Cards */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            background: theme === "dark"
                                ? "linear-gradient(135deg, #7d9572, #5c7354)"
                                : "linear-gradient(135deg, #AFC9A5, #8AA172)",
                            color: theme === "dark" ? "#E3E3E3" : "#1C1C1C",
                            borderRadius: "12px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            padding: "10px",
                            textAlign: "center",
                       }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: "bold", opacity: 0.9 }}>
                                    Total Carbon Credits Earned
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                                    {totalEarned.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            background: theme === "dark"
                                ? "linear-gradient(135deg, #A39B86, #8B8370)"
                                : "linear-gradient(135deg, #D2C7AE, #B0A084)",
                            color: theme === "dark" ? "#E3E3E3" : "#1C1C1C",
                            borderRadius: "12px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            padding: "10px",
                            textAlign: "center",
                        }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: "bold", opacity: 0.9 }}>
                                    Available Credits
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                                    {totalAvailable.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Card sx={{
                            background: theme === "dark"
                                ? "linear-gradient(135deg, #6E7864, #53574f)"
                                : "linear-gradient(135deg, #929D8B, #6E7864)",
                            color: theme === "dark" ? "#E3E3E3" : "#1C1C1C",
                            borderRadius: "12px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            padding: "10px",
                            textAlign: "center",
                        }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: "bold", opacity: 0.9 }}>
                                    Revenue from Carbon Credits
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                                    ${totalRevenue.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Carbon Credit Chart */}
                <Grid item xs={12} sx={{ marginTop: "20px" }}>
                    <Card sx={{
                        background: theme === "dark" ? "#4E5548" : "#E1E4DD",
                        color: theme === "dark" ? "#ffffff" : "#000000",
                    }}>
                        <CardContent>
                            <Typography variant="h6">Carbon Credit Overview</Typography>
                            {chartData ? (
                                <Bar data={chartData} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
                            ) : (
                                <Typography>Loading chart...</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Box>
        </Box>
    );
};

export default Dashboard;
